import { toSnakeCase } from '@/tracking/amplitude/utils';
import type { TrackingEvent } from '@/tracking/tracking-manager';
import {
    REWARDS_HUB_AMP_EVENT,
    REWARDS_MODULE,
    REWARDS_SOURCE,
    REWARDS_BOX_STATUS,
    REWARDS_PAGE_NAME
} from './event-names';

export type RewardsHubSharedState = {
    rewardsBonusDollarAmount: number | null;
    rewardsBonusSpinsAmount: number | null;
    rewardsLoyaltyPointsAmount: number | null;
    rewardsLoyaltyLevel: string | undefined;
};

export type PromoBaseContext = {
    promoId?: string;
    promoName?: string;
};

export type PromoHubCardViewedContext = PromoBaseContext & {
    position?: number;
    location?: string;
    tab?: string;
};

export type RewardsHubTrackSendEvent = (events: TrackingEvent[]) => void;

export const getModuleByLocation = (location?: string): string => {
    if (location === 'available') {
        return REWARDS_MODULE.AVAILABLE_PROMOS;
    } else if (location === 'completed') {
        return REWARDS_MODULE.COMPLETED_PROMOS;
    } else if (location === 'learn_more') {
        return REWARDS_MODULE.LEARN_MORE;
    } else if (location === 'carousel') {
        return REWARDS_MODULE.HERO_CAROUSEL;
    }
    return REWARDS_MODULE.PROMO_HUB_HOME;
};

export const getOptInModule = (location?: string): string => {
    if (location === 'carousel') {
        return REWARDS_MODULE.HERO_CAROUSEL;
    }
    if (location === 'offer') {
        return REWARDS_MODULE.PROMO_HUB_OFFER;
    }
    return REWARDS_MODULE.PROMO_HUB_HOME;
};

export const getBoxAvailableStatus = (boxesNumber: number) => {
    if (boxesNumber === 0) return REWARDS_BOX_STATUS.NONE;
    if (boxesNumber === 1) return REWARDS_BOX_STATUS.SINGLE;
    if (boxesNumber > 1) return REWARDS_BOX_STATUS.MULTIPLE;
    return REWARDS_BOX_STATUS.UNKNOWN;
};

export const mapRewardsHubSharedStateToAmplitude = (sharedState: RewardsHubSharedState | null | undefined) => {
    if (!sharedState) {
        return {};
    }

    return {
        'Casino Bonus Dollar Amount': sharedState.rewardsBonusDollarAmount,
        'Casino Bonus Spins Amount': sharedState.rewardsBonusSpinsAmount,
        'Casino Loyalty Points Amount': sharedState.rewardsLoyaltyPointsAmount,
        'Casino Loyalty Level': sharedState.rewardsLoyaltyLevel
    };
};

export const mapPromoBaseContextToAmplitudeProps = (context?: PromoBaseContext): Record<string, unknown> => ({
    ...(context?.promoId ? { 'Promo ID': context.promoId.toLowerCase() } : {}),
    ...(context?.promoName ? { 'Promo Name': toSnakeCase(context.promoName) } : {})
});

export const mapPositionToAmplitudeProps = (position?: number | null): Record<string, unknown> =>
    position !== null && position !== undefined ? { Position: position } : {};

export const trackRewardsHubAmplitude = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    event: string;
    props?: Record<string, unknown>;
    sharedState?: RewardsHubSharedState | null;
}) => {
    const { sendEvent, event, props = {}, sharedState } = args;

    if (!sendEvent) {
        return;
    }

    sendEvent([
        {
            destination: 'amplitude',
            eventProps: {
                event,
                Module: REWARDS_MODULE.REWARDS_HUB, // do not change; override per area
                'Page Name': REWARDS_PAGE_NAME,
                ...mapRewardsHubSharedStateToAmplitude(sharedState),
                ...props
            }
        }
    ]);
};

export const trackPromoHubCardsAllViewedToAmplitude = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    sharedState?: RewardsHubSharedState | null;
}) => {
    trackRewardsHubAmplitude({
        sendEvent: args.sendEvent,
        event: REWARDS_HUB_AMP_EVENT.PROMO_HUB_CARDS_ALL_VIEWED,
        sharedState: args.sharedState,
        props: {
            Module: REWARDS_MODULE.PROMO_HUB_HOME
        }
    });
};

export type RewardsHubAnalyticsContext = {
    pageName?: string;
    module?: string;
};

// In-memory de-duplication for promo card viewed events (resets on page refresh)
const viewedPromoCards = new Set<string>();

export const trackPromoCardViewedToAmplitude = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    sharedState?: RewardsHubSharedState | null;
    context?: PromoHubCardViewedContext;
    analyticsContext?: RewardsHubAnalyticsContext;
}) => {
    const { sendEvent, sharedState, context, analyticsContext } = args;

    if (!sendEvent) return;

    const viewedKey = `promo_card_viewed:${context?.promoId?.toLowerCase() ?? 'unknown'}:${context?.location ?? 'unknown'}`;

    if (viewedPromoCards.has(viewedKey)) {
        return;
    }
    viewedPromoCards.add(viewedKey);

    const tab = context?.tab ?? context?.location;

    trackRewardsHubAmplitude({
        sendEvent,
        event: REWARDS_HUB_AMP_EVENT.PROMO_CARD_VIEWED,
        sharedState,
        props: {
            Module: analyticsContext?.module ?? getModuleByLocation(context?.location),
            Source: REWARDS_SOURCE.PROMO_HUB,
            ...(analyticsContext?.pageName ? { 'Page Name': analyticsContext.pageName } : {}),
            ...(tab ? { Tab: tab } : {}),
            ...mapPromoBaseContextToAmplitudeProps(context),
            ...mapPositionToAmplitudeProps(context?.position)
        }
    });
};

export type PromoOptInContext = PromoBaseContext & {
    position?: number;
    location?: string;
};

type PromoOptInTrackArgs = {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    sharedState?: RewardsHubSharedState | null;
    context?: PromoOptInContext;
};

const mapPromoOptInContextToAmplitudeProps = (context?: PromoOptInContext): Record<string, unknown> => {
    const moduleName = getOptInModule(context?.location);
    const isCarousel = moduleName === REWARDS_MODULE.HERO_CAROUSEL;

    return {
        Module: moduleName,
        ...mapPromoBaseContextToAmplitudeProps(context),
        ...(isCarousel ? mapPositionToAmplitudeProps(context?.position) : {})
    };
};

const trackPromoOptInToAmplitude = (args: PromoOptInTrackArgs & { event: string }) => {
    trackRewardsHubAmplitude({
        sendEvent: args.sendEvent,
        event: args.event,
        sharedState: args.sharedState,
        props: mapPromoOptInContextToAmplitudeProps(args.context)
    });
};

export const trackPromoOptInFailedToAmplitude = (args: PromoOptInTrackArgs) => {
    trackPromoOptInToAmplitude({ ...args, event: REWARDS_HUB_AMP_EVENT.PROMO_OPT_IN_FAILED });
};

export const trackPromoOptInSuccessfulToAmplitude = (args: PromoOptInTrackArgs) => {
    trackPromoOptInToAmplitude({ ...args, event: REWARDS_HUB_AMP_EVENT.PROMO_OPT_IN_SUCCESSFUL });
};

export const trackRewardsHubLinkEvent = (args: {
    sendEvent: RewardsHubTrackSendEvent | undefined;
    event: string;
    linkText: string;
    linkUrl?: string;
    eventLabel?: string;
    rewardAmount?: string | number;
    sharedState?: RewardsHubSharedState | null;
    extra?: Record<string, unknown>;
}) => {
    const { sendEvent, event, linkText, linkUrl, eventLabel, rewardAmount, sharedState, extra = {} } = args;

    trackRewardsHubAmplitude({
        sendEvent,
        event,
        sharedState,
        props: {
            'Link Text': toSnakeCase(linkText),
            ...(linkUrl ? { 'Link URL': linkUrl } : {}),
            ...(eventLabel ? { 'Event Label': toSnakeCase(eventLabel) } : {}),
            ...(rewardAmount ? { 'Reward Amount': rewardAmount } : {}),
            ...extra
        }
    });
};
